import { FC, Ref, useEffect, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { Params } from 'next/dist/server/request/params';

import { PRODUCT_FILTER_ATTRIBUTES } from '@/constants';
import { useBundleSpecial, useDispensaryTimeZone } from '@/hooks';
import {
  CollectionPlaceholder,
  CollectionTreezHeading,
  Container,
  Heading,
  Icon,
  Placeholder,
  Product,
  SpecialBanner,
  SpecialCounter,
  useData,
} from '@/components';
import { useAnalytics } from '@/data';
import {
  useInfiniteHits,
  useMapProduct,
  useOpenSearchContext,
} from '@/opensearch';
import { GProduct, OpenSearchProduct } from '@/types';
import { buildListingRouteKey } from '@/utils';

import styles from './CollectionTreez.module.scss';
import { CollectionTreezPagination } from './CollectionTreezPagination';

const LISTING_ANCHOR_SCROLL_TOLERANCE = 150;

interface ICollectionTreez {
  bundleId?: string;
  onSpecials?: boolean;
  bundleSlug?: string;
  bundleTitle?: string;
}

const CollectionTreez: FC<ICollectionTreez> = ({
  bundleId,
  onSpecials = false,
  bundleSlug,
  bundleTitle,
}) => {
  const { measureViewItemsListGA4 } = useAnalytics();
  const { productCard } = useData();
  const { spacingClass, isLargeCard, isProductCardLarge } =
    productCard.displayProps;
  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();
  const { hits, isLoading, isFetching } = useInfiniteHits<OpenSearchProduct>();
  const { isLinkNavigationPending, sortFilter, searchFilter } =
    useOpenSearchContext();
  const timeZone = useDispensaryTimeZone();
  const { mapProduct } = useMapProduct();
  const products: GProduct[] = hits.map(hit => ({
    ...mapProduct({
      product: hit,
      isSortingApplied: !!sortFilter,
      isSearchingApplied: !!searchFilter,
      timeZone,
    }),
    bundle_id: bundleId,
    bundle_title: bundleTitle,
    bundle_slug: bundleSlug,
  }));

  const isLoadingProducts = isLoading || isFetching || isLinkNavigationPending;

  const {
    initDepProducts,
    initIndProducts,
    processingDependents,
    processingIndependents,
    special,
  } = useBundleSpecial({
    id: bundleId!,
  });

  const headingRef = useRef<HTMLDivElement | undefined>(undefined);
  const sectionRef = useRef<HTMLDivElement | undefined>(undefined);
  const prevListingKeyRef = useRef<string | null>(null);

  const openSearchListingKey = buildListingRouteKey(
    pathname,
    params,
    searchParams.entries(),
    PRODUCT_FILTER_ATTRIBUTES._PAGE,
  );

  const getListingAnchorTop = () => {
    const anchor = headingRef.current;
    if (!anchor) return null;

    const headerOffset =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--header-height',
        ),
      ) || 0;

    return Math.max(0, anchor.offsetTop - headerOffset);
  };

  const scrollToListingAnchor = () => {
    const anchorTop = getListingAnchorTop();
    if (anchorTop === null) return;

    window.scrollTo({ top: anchorTop, behavior: 'auto' });
  };

  const isNearListingAnchor = () => {
    const anchorTop = getListingAnchorTop();
    if (anchorTop === null) return true;

    return (
      Math.abs(window.scrollY - anchorTop) < LISTING_ANCHOR_SCROLL_TOLERANCE
    );
  };

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const isFirstRender = prevListingKeyRef.current === null;
    const listingKeyChanged =
      !isFirstRender && prevListingKeyRef.current !== openSearchListingKey;
    prevListingKeyRef.current = openSearchListingKey;

    if (!listingKeyChanged || isNearListingAnchor()) return;

    scrollToListingAnchor();
  }, [openSearchListingKey]);

  const handlePageChange = () => {
    if (sectionRef && sectionRef.current) {
      window.scrollTo({
        top: sectionRef.current.offsetTop - 320,
        behavior: 'auto',
      });
    }
  };

  useEffect(() => {
    const analyticsListName = getListName(pathname as string, params as Params);
    measureViewItemsListGA4(products, analyticsListName);
  }, [products]);

  const getListName = (pathname: string, params: Params) => {
    const listTitle = pathname.split('/')[1];
    const listSubtitle = params?.slug;
    return `${listTitle}${listSubtitle ? `_${listSubtitle}` : ''}`;
  };

  const renderV = () => {
    if (isLoadingProducts) {
      return (
        <div className={styles.placeholder__container}>
          {onSpecials && isLoading && (
            <Placeholder className={styles.placeholder__banner} />
          )}
          {onSpecials && !isLoading && special && (
            <div className={styles.special_description}>
              <SpecialBanner bundleId={bundleId!} />
              <SpecialCounter bundleId={bundleId!} />
            </div>
          )}
          <CollectionPlaceholder />
        </div>
      );
    }

    if (
      (!isLoadingProducts && !products.length) ||
      (processingDependents && initDepProducts?.length === 0) ||
      (processingIndependents && initIndProducts?.length === 0)
    ) {
      return (
        <div className={styles.hits__empty}>
          <Icon name="search" />
          <Heading level={5}>No search results found</Heading>
          <p>
            No results match the filter criteria. Remove a filter or clear all
            filters to search again
          </p>
        </div>
      );
    }

    return (
      <>
        {onSpecials && special && (
          <div className={styles.special_description}>
            <SpecialBanner bundleId={bundleId!} />
            <SpecialCounter bundleId={bundleId!} />
          </div>
        )}

        <div
          className={classNames(styles.collection_hits__list, spacingClass, {
            [styles.collection_hits__list_card_large]: isLargeCard,
            [styles.collection_hits__list_card_large_variation]:
              isProductCardLarge,
          })}
        >
          {products.map(item => (
            <Product
              className={styles.collection_hits__list_item}
              key={item.id}
              product={item}
            />
          ))}
        </div>
        <CollectionTreezPagination onPageChange={handlePageChange} />
      </>
    );
  };

  return (
    <section className={styles.collection_section}>
      <div ref={headingRef as Ref<HTMLDivElement> | undefined}>
        <CollectionTreezHeading />
      </div>
      <Container
        className={classNames(styles.collection_container, {
          [styles.container__no_side_spaces]: spacingClass,
        })}
      >
        <div
          className={classNames(styles.collection, {
            [styles['collection_hits--onSpecials']]: onSpecials,
          })}
          ref={sectionRef as Ref<HTMLDivElement> | undefined}
        >
          {renderV()}
        </div>
      </Container>
    </section>
  );
};

export { CollectionTreez };
